import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRank, setFilterRank] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortUsers = useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm) ||
        user.mySponsorCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => 
        filterStatus === 'active' ? user.isActive : !user.isActive
      );
    }

    // Rank filter
    if (filterRank !== 'all') {
      filtered = filtered.filter(user => user.rank === filterRank);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'totalBusiness' || sortBy === 'purchaseCount') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterStatus, filterRank, sortBy, sortOrder]);

  useEffect(() => {
    filterAndSortUsers();
  }, [filterAndSortUsers]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      password: user.password || '',
      transactionPassword: user.transactionPassword || '',
      rank: user.rank || '',
      isActive: user.isActive,
      bankDetails: {
        accountHolder: user.bankDetails?.accountHolder || '',
        accountNumber: user.bankDetails?.accountNumber || '',
        bankName: user.bankDetails?.bankName || '',
        branchName: user.bankDetails?.branchName || '',
        ifscCode: user.bankDetails?.ifscCode || '',
        verified: user.bankDetails?.verified || false
      }
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const handleEditSave = async () => {
    try {
      setEditLoading(true);
      await userAPI.editUser(selectedUser._id, editFormData);
      alert('User updated successfully');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      alert('Error updating user: ' + (error.response?.data?.error || error.message));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setEditLoading(true);
      await userAPI.deleteUser(selectedUser._id);
      alert('User deleted successfully');
      setShowDeleteConfirm(false);
      fetchUsers();
    } catch (error) {
      alert('Error deleting user: ' + (error.response?.data?.error || error.message));
    } finally {
      setEditLoading(false);
    }
  };

  const getUniqueRanks = () => {
    const ranks = [...new Set(users.map(user => user.rank).filter(Boolean))];
    return ranks;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] p-6 rounded-2xl">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-[#0F2F3D]">
            User Management
          </h2>
          <p className="text-gray-500 mt-1">
            Manage and monitor all platform users ({filteredUsers.length} of {users.length})
          </p>
        </div>

        <button
          onClick={fetchUsers}
          className="px-4 py-2 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg shadow hover:opacity-90 transition-all duration-300"
        >
          Refresh
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          
          {/* Search */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Search by name, email, phone, or sponsor code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Rank Filter */}
          <select
            value={filterRank}
            onChange={(e) => setFilterRank(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
          >
            <option value="all">All Ranks</option>
            {getUniqueRanks().map(rank => (
              <option key={rank} value={rank}>{rank}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              setSortBy(sort);
              setSortOrder(order);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="totalBusiness-desc">Business (High to Low)</option>
            <option value="totalBusiness-asc">Business (Low to High)</option>
            <option value="purchaseCount-desc">Purchases (High to Low)</option>
            <option value="purchaseCount-asc">Purchases (Low to High)</option>
          </select>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col max-h-[75vh]">

        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full">

            {/* Table Head */}
            <thead className="bg-[#0F2F3D] text-white sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">SR. No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Password</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Txn Password</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Rank</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Business</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Purchases</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Sponsor</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Joined</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr
                    key={index}
                    className="hover:bg-[#E4572E]/5 transition-all cursor-pointer"
                    onClick={() => handleUserClick(user)}
                  >
                    <td className="px-6 py-4 font-medium text-[#0F2F3D]">{index + 1}</td>
                    <td className="px-6 py-4 font-medium text-[#0F2F3D]">
                      <div>
                        <div className="font-semibold">{user.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{user.mySponsorCode}</div>
                        <div className="text-xs text-blue-600">Level {user.level || 1}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>
                        <div>{user.email}</div>
                        {user.lastLogin && (
                          <div className="text-xs text-gray-400">
                            Last: {new Date(user.lastLogin).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.phone}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                      {user.password || 'N/A'}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                      {user.transactionPassword || 'N/A'}
                    </td>

                    <td className="px-6 py-4 font-semibold text-[#E4572E]">
                      <div>
                        <div>{user.rank || 'Not Ranked'}</div>
                        {user.rankAchievedDate && (
                          <div className="text-xs text-gray-400">
                            {new Date(user.rankAchievedDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-[#0F2F3D]">₹{(user.totalBusiness || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">
                          Self: ₹{(user.selfBusiness || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Team: ₹{(user.teamBusiness || 0).toLocaleString()}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <div className="text-lg font-bold text-[#E4572E]">
                          {user.purchaseCount || 0}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.purchaseCount > 0 ? 'Purchases' : 'No purchases'}
                        </div>
                        {user.purchaseCount > 0 && (
                          <div className="mt-1">
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              Active
                            </span>
                          </div>
                        )}
                        {user.purchaseCount === 0 && (
                          <div className="mt-1">
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              Inactive
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.sponsorId ? (
                        <div>
                          <div className="font-medium">{user.sponsorId.name}</div>
                          <div className="text-xs text-gray-400">{user.sponsorId.email}</div>
                          <div className="text-xs font-mono text-blue-600">{user.sponsorId.mySponsorCode}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>
                        <div>{new Date(user.createdAt).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-400">
                          {user.directSponsees?.length || 0} direct
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            user.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                          title={`User is ${user.isActive ? 'Active' : 'Inactive'} based on purchase history`}
                        >
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {user.isBlocked && (
                          <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 ml-1">
                            Blocked
                          </span>
                        )}
                        <div className="text-xs text-gray-400">
                          KYC: {user.kycStatus || 'PENDING'}
                        </div>
                        <div className="text-xs text-blue-600">
                          📦 Based on {user.purchaseCount || 0} purchases
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(user);
                          }}
                          className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(user);
                          }}
                          className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="12"
                    className="text-center py-10 text-gray-500"
                  >
                    {searchTerm || filterStatus !== 'all' || filterRank !== 'all' 
                      ? 'No users found matching your criteria.' 
                      : 'No users found.'
                    }
                  </td>
                </tr>
              )}
            </tbody>

          </table>
        </div>

      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-[#0F2F3D]">User Details</h3>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Personal Information</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {selectedUser.name}</p>
                    <p><span className="font-medium">Email:</span> {selectedUser.email}</p>
                    <p><span className="font-medium">Phone:</span> {selectedUser.phone}</p>
                    <p><span className="font-medium">Sponsor Code:</span> {selectedUser.mySponsorCode || 'N/A'}</p>
                    <p><span className="font-medium">Joined:</span> {new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                    <p><span className="font-medium">Purchase Count:</span> {selectedUser.purchaseCount || 0}</p>
                    <p><span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        selectedUser.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {selectedUser.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="ml-2 text-xs text-blue-600">
                        (Based on purchases)
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Business Information</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Rank:</span> {selectedUser.rank || 'Not Ranked'}</p>
                    <p><span className="font-medium">Total Business:</span> ₹{(selectedUser.totalBusiness || 0).toLocaleString()}</p>
                    <p><span className="font-medium">Sponsor:</span> {selectedUser.sponsorId?.name || 'None'}</p>
                    <p><span className="font-medium">Direct Members:</span> {selectedUser.directSponsees?.length || 0}</p>
                    {selectedUser.wallet && (
                      <>
                        <p><span className="font-medium">Total Earned:</span> ₹{(selectedUser.wallet.totalEarned || 0).toLocaleString()}</p>
                        <p><span className="font-medium">Pending Amount:</span> ₹{(selectedUser.wallet.pendingAmount || 0).toLocaleString()}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-[#0F2F3D]">Edit User</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Login Password</label>
                  <input
                    type="text"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Password</label>
                  <input
                    type="text"
                    value={editFormData.transactionPassword}
                    onChange={(e) => setEditFormData({ ...editFormData, transactionPassword: e.target.value })}
                    placeholder="Leave blank to keep current"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rank</label>
                  <input
                    type="text"
                    value={editFormData.rank}
                    onChange={(e) => setEditFormData({ ...editFormData, rank: e.target.value })}
                    placeholder="e.g., Star, Gold, Platinum"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                  />
                </div>

                {/* Bank Details Section */}
                <div className="pt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Bank Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder</label>
                      <input
                        type="text"
                        value={editFormData.bankDetails.accountHolder}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, accountHolder: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                      <input
                        type="text"
                        value={editFormData.bankDetails.accountNumber}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, accountNumber: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={editFormData.bankDetails.bankName}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, bankName: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
                      <input
                        type="text"
                        value={editFormData.bankDetails.branchName}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, branchName: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                      <input
                        type="text"
                        value={editFormData.bankDetails.ifscCode}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, ifscCode: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]/20 focus:border-[#E4572E]"
                      />
                    </div>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={editFormData.bankDetails.verified}
                        onChange={e => setEditFormData({
                          ...editFormData,
                          bankDetails: { ...editFormData.bankDetails, verified: e.target.checked }
                        })}
                        className="h-4 w-4 text-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">Bank Verified</label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editFormData.isActive}
                    onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                    className="h-4 w-4 text-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">Active</label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleEditSave}
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all duration-300"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-red-600 mb-4">Delete User</h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>{selectedUser.name}</strong> ({selectedUser.mySponsorCode})? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteConfirm}
                  disabled={editLoading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-all duration-300"
                >
                  {editLoading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={editLoading}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsers;